import React, { useMemo } from 'react'
import { Route, Switch, useLocation, useHistory, Link } from 'react-router-dom'
import { Layout, Menu, Breadcrumb } from 'antd'
import { CompassOutlined, DollarCircleOutlined } from '@ant-design/icons'
import {
	IncomesExplorePage,
	IncomeDetailPage,
	IncomesTablePage,
} from '../../pages'
import {
	incomesRoutes,
	incomesNoHeaderMatchingRegexes,
	IIncomeRoute,
} from './routes'
import { snakeCaseToTitle } from '../../util'

const breadcrumbsIndexesToShowNames = [
	{
		i: 6,
		show: 'institutionName',
	},
	{
		i: 8,
		show: 'entityName',
	},
	{
		i: 10,
		show: 'programName',
	},
]

const IncomesRoutes: React.FC = () => {
	const location = useLocation<any>()
	const history = useHistory()
	const selectKey = useMemo(
		() => (location.pathname.includes('discretos') ? 'discretos' : 'explore'),
		[location.pathname]
	)

	const handleDiscreteClick = (param: any) => {
		if (param.key === 'discretos' && !location.pathname.includes('discretos')) {
			history.push(`${location.pathname}/discretos`, location.state)
		}

		if (param.key !== 'discretos' && location.pathname.includes('discretos')) {
			const pathArray = location.pathname.split('/')
			const newURL = pathArray.slice(0, pathArray.length - 1).join('/')
			history.push(newURL, location.state)
		}
	}

	const hasNoHeader = useMemo(
		() =>
			incomesNoHeaderMatchingRegexes.some((patter) =>
				patter.test(location.pathname)
			),
		[location.pathname]
	)

	const getComponentToRender = (route: IIncomeRoute) => {
		switch (route.component) {
			case 'aggregation':
				return <IncomesExplorePage level={route.level} />

			case 'list':
				return <IncomesTablePage level={route.level} />

			case 'detail':
				return <IncomeDetailPage />

			default:
				throw Error('Invalid route level')
		}
	}

	const pathSnippets = location.pathname.split('/').filter((i) => i) // filter out falsies
	const breadcrumbs = pathSnippets.map((snippet, i) => {
		const url = `/${pathSnippets.slice(0, i + 1).join('/')}`
		const lastSnippet = pathSnippets[pathSnippets.length - 1]
		let isClickable: boolean = false
		if (pathSnippets.length > 1) {
			isClickable = i % 2 === 0 && lastSnippet !== snippet
		}

		const showName = breadcrumbsIndexesToShowNames.find(
			(value) => value.i === i
		)

		let textToShow = showName
			? location.state
				? location.state[showName.show] || snippet
				: snippet
			: snippet

		if (i === 0 || i === 8 || i === 10) {
			textToShow = snakeCaseToTitle(textToShow)
		}

		return (
			<Breadcrumb.Item key={url}>
				<Link
					to={url}
					onClick={(e: any) => {
						e.preventDefault()
						if (isClickable) {
							const pushTo = e.target.pathname.replace('%C3%B1', 'ñ') // had to override default Link behaviour to preserve location state
							history.push(pushTo, location.state)
						}
					}}
					style={{
						cursor: isClickable ? 'pointer' : 'default',
						color: isClickable ? '#1890ff' : 'gray',
					}}
				>
					{textToShow}
				</Link>
			</Breadcrumb.Item>
		)
	})

	return (
		<Layout style={{  marginLeft: '2rem' }}>
			{breadcrumbs.length > 1 && (
				<Breadcrumb style={{ marginBottom: '1rem' }}>{breadcrumbs}</Breadcrumb>
			)}
			{!hasNoHeader && (
				<Layout.Header
					style={{
						width: '100%',
						background: 'white',
						marginBottom: '1rem',
						borderRadius: '5px',
						lineHeight: '4.5em',
					}}
				>
					<Menu mode='horizontal' selectedKeys={[selectKey]}>
						<Menu.Item
							key='explore'
							icon={<CompassOutlined />}
							onClick={handleDiscreteClick}
						>
							Navegar
						</Menu.Item>
						<Menu.Item
							key='discretos'
							icon={<DollarCircleOutlined />}
							onClick={handleDiscreteClick}
						>
							Discretos
						</Menu.Item>
					</Menu>
				</Layout.Header>
			)}

			<Layout.Content>
				<Switch>
					{incomesRoutes.map((route) => (
						<Route path={route.path} key={route.path} exact>
							{getComponentToRender(route)}
						</Route>
					))}
				</Switch>
			</Layout.Content>
		</Layout>
	)
}

export default IncomesRoutes
